import { useState, useEffect, useCallback } from 'react';

/**
 * Device Security Scanner
 * Detects potential spyware, screen recording, and suspicious browser extensions
 */

// Known suspicious extension patterns (content scripts, DOM modifications)
const SUSPICIOUS_EXTENSION_PATTERNS = [
  'screen-capture',
  'screenshot',
  'recorder',
  'spy',
  'keylog',
  'monitor',
  'track',
  'snoop',
  'capture',
  'record',
];

// Check for screen recording/capture APIs being used
export const useScreenRecordingDetection = () => {
  const [isScreenBeingRecorded, setIsScreenBeingRecorded] = useState(false);
  const [recordingWarning, setRecordingWarning] = useState(null);

  useEffect(() => {
    // Check if getDisplayMedia is being called
    const originalGetDisplayMedia = navigator.mediaDevices?.getDisplayMedia;
    
    if (originalGetDisplayMedia) {
      navigator.mediaDevices.getDisplayMedia = async function(...args) {
        setIsScreenBeingRecorded(true);
        setRecordingWarning('Screen sharing/recording detected!');
        return originalGetDisplayMedia.apply(this, args);
      };
    }

    // Check for Screen Capture API support and active captures
    const checkScreenCapture = async () => {
      try {
        // Check if there's an active screen capture permission
        const permissions = await navigator.permissions?.query({ name: 'display-capture' });
        if (permissions?.state === 'granted') {
          setRecordingWarning('Screen capture permission is granted to some application');
        }
      } catch (e) {
        // Permission API might not support display-capture query
      }
    };

    checkScreenCapture();

    // Monitor for MediaRecorder usage (indicates recording)
    const originalMediaRecorder = window.MediaRecorder;
    if (originalMediaRecorder) {
      window.MediaRecorder = class extends originalMediaRecorder {
        constructor(stream, options) {
          super(stream, options);
          // Check if it's recording a screen stream
          const tracks = stream.getTracks();
          const hasDisplayTrack = tracks.some(track => 
            track.label.toLowerCase().includes('screen') || 
            track.label.toLowerCase().includes('window') ||
            track.label.toLowerCase().includes('display')
          );
          if (hasDisplayTrack) {
            setIsScreenBeingRecorded(true);
            setRecordingWarning('Screen recording in progress');
          }
        }
      };
    }

    return () => {
      // Restore originals on cleanup
      if (originalGetDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = originalGetDisplayMedia;
      }
      if (originalMediaRecorder) {
        window.MediaRecorder = originalMediaRecorder;
      }
    };
  }, []);

  return { isScreenBeingRecorded, recordingWarning };
};

// Detect suspicious browser extensions
export const useExtensionScanner = () => {
  const [suspiciousExtensions, setSuspiciousExtensions] = useState([]);
  const [scanComplete, setScanComplete] = useState(false);
  const [extensionCount, setExtensionCount] = useState(0);

  const scanForExtensions = useCallback(async () => {
    const detected = [];
    
    // Method 1: Check for common extension-injected elements
    const extensionElements = document.querySelectorAll('[data-extension-id], [class*="extension"], [id*="extension"]');
    if (extensionElements.length > 0) {
      detected.push({
        type: 'dom_injection',
        message: `Found ${extensionElements.length} extension-injected elements`,
        severity: 'medium'
      });
    }

    // Method 2: Check for extension protocol resources
    const checkExtensionResource = async (protocol) => {
      try {
        const testUrls = [
          `${protocol}://test-extension-id/`,
        ];
        // This will fail but we can detect certain behaviors
      } catch (e) {
        // Expected: most browsers throw here. Failure is the normal path
        // for this probe — silence is correct.
      }
    };

    // Method 3: Check for modified prototypes (extension tampering)
    const nativeToString = Function.prototype.toString;
    const suspiciousModifications = [];
    
    const functionsToCheck = [
      { obj: document, name: 'createElement' },
      { obj: window, name: 'fetch' },
      { obj: XMLHttpRequest.prototype, name: 'open' },
      { obj: XMLHttpRequest.prototype, name: 'send' },
    ];

    functionsToCheck.forEach(({ obj, name }) => {
      try {
        const fnString = nativeToString.call(obj[name]);
        if (!fnString.includes('[native code]')) {
          suspiciousModifications.push(name);
        }
      } catch (e) {
        // Expected: accessing some prototype methods throws in sandboxed
        // iframes / strict CSP. That's not a tamper signal — absence
        // of `[native code]` is.
      }
    });

    if (suspiciousModifications.length > 0) {
      detected.push({
        type: 'prototype_tampering',
        message: `Modified browser functions detected: ${suspiciousModifications.join(', ')}`,
        severity: 'high'
      });
    }

    // Method 4: Check for suspicious global variables
    const suspiciousGlobals = [];
    SUSPICIOUS_EXTENSION_PATTERNS.forEach(pattern => {
      Object.keys(window).forEach(key => {
        if (key.toLowerCase().includes(pattern)) {
          suspiciousGlobals.push(key);
        }
      });
    });

    if (suspiciousGlobals.length > 0) {
      detected.push({
        type: 'suspicious_globals',
        message: `Suspicious global variables: ${suspiciousGlobals.slice(0, 5).join(', ')}`,
        severity: 'medium'
      });
    }

    // Method 5: Check for content script injections via MutationObserver artifacts
    const checkForInjections = () => {
      const scripts = document.querySelectorAll('script[src*="extension"], script[src*="chrome-extension"], script[src*="moz-extension"]');
      if (scripts.length > 0) {
        detected.push({
          type: 'injected_scripts',
          message: `Found ${scripts.length} extension-injected scripts`,
          severity: 'high'
        });
      }
    };
    checkForInjections();

    // Method 6: Estimate extension count via timing attacks (approximate)
    const estimateExtensionCount = async () => {
      // Extensions often add event listeners that slow down certain operations
      const start = performance.now();
      for (let i = 0; i < 100; i++) {
        document.dispatchEvent(new CustomEvent('__ghostcomm_probe__'));
      }
      const duration = performance.now() - start;
      // Higher duration might indicate more extensions listening
      const estimatedCount = Math.floor(duration / 2); // Rough estimate
      return Math.min(estimatedCount, 20); // Cap at reasonable number
    };

    const count = await estimateExtensionCount();
    setExtensionCount(count);

    setSuspiciousExtensions(detected);
    setScanComplete(true);
    
    return detected;
  }, []);

  useEffect(() => {
    // Run initial scan
    const timer = setTimeout(scanForExtensions, 1000);
    
    // Re-scan periodically
    const interval = setInterval(scanForExtensions, 30000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [scanForExtensions]);

  return { suspiciousExtensions, scanComplete, extensionCount, rescan: scanForExtensions };
};

// Combined device security hook
export const useDeviceSecurityScan = () => {
  const { isScreenBeingRecorded, recordingWarning } = useScreenRecordingDetection();
  const { suspiciousExtensions, scanComplete, extensionCount, rescan } = useExtensionScanner();
  const [clipboardAccess, setClipboardAccess] = useState(false);
  const [cameraAccess, setCameraAccess] = useState(false);
  const [microphoneAccess, setMicrophoneAccess] = useState(false);

  // Check for clipboard access
  useEffect(() => {
    const checkClipboard = async () => {
      try {
        const permission = await navigator.permissions?.query({ name: 'clipboard-read' });
        setClipboardAccess(permission?.state === 'granted');
      } catch (e) {
        // Safari + older Firefox throw for unrecognized permission names.
        // Treating that as "permission not granted" is the correct default.
      }
    };
    checkClipboard();
  }, []);

  // Check for camera/mic access
  useEffect(() => {
    const checkMediaPermissions = async () => {
      try {
        const [camera, mic] = await Promise.all([
          navigator.permissions?.query({ name: 'camera' }),
          navigator.permissions?.query({ name: 'microphone' })
        ]);
        setCameraAccess(camera?.state === 'granted');
        setMicrophoneAccess(mic?.state === 'granted');
      } catch (e) {
        // Same as clipboard above: Safari + older Firefox throw for
        // these permission names. Absence of permission is the safe
        // default.
      }
    };
    checkMediaPermissions();
    
    // Re-check when permissions change
    const interval = setInterval(checkMediaPermissions, 5000);
    return () => clearInterval(interval);
  }, []);

  // Calculate overall threat level
  const getThreatLevel = useCallback(() => {
    let score = 0;
    
    if (isScreenBeingRecorded) score += 50;
    if (suspiciousExtensions.some(e => e.severity === 'high')) score += 30;
    if (suspiciousExtensions.some(e => e.severity === 'medium')) score += 15;
    if (extensionCount > 10) score += 10;
    if (clipboardAccess) score += 5;
    
    if (score >= 50) return { level: 'critical', color: 'text-red-500', bgColor: 'bg-red-500/10' };
    if (score >= 25) return { level: 'warning', color: 'text-amber-500', bgColor: 'bg-amber-500/10' };
    if (score >= 10) return { level: 'caution', color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' };
    return { level: 'secure', color: 'text-emerald-500', bgColor: 'bg-emerald-500/10' };
  }, [isScreenBeingRecorded, suspiciousExtensions, extensionCount, clipboardAccess]);

  return {
    isScreenBeingRecorded,
    recordingWarning,
    suspiciousExtensions,
    scanComplete,
    extensionCount,
    clipboardAccess,
    cameraAccess,
    microphoneAccess,
    threatLevel: getThreatLevel(),
    rescan
  };
};

// Security alert component data
export const getSecurityAlerts = (securityData) => {
  const alerts = [];
  
  if (securityData.isScreenBeingRecorded) {
    alerts.push({
      id: 'screen-recording',
      type: 'critical',
      title: 'Screen Recording Detected',
      message: securityData.recordingWarning || 'Your screen may be being recorded',
      icon: 'video'
    });
  }
  
  securityData.suspiciousExtensions.forEach((ext, idx) => {
    alerts.push({
      id: `extension-${idx}`,
      type: ext.severity === 'high' ? 'critical' : 'warning',
      title: 'Suspicious Extension Activity',
      message: ext.message,
      icon: 'puzzle'
    });
  });
  
  if (securityData.extensionCount > 10) {
    alerts.push({
      id: 'many-extensions',
      type: 'warning',
      title: 'Many Extensions Detected',
      message: `${securityData.extensionCount}+ browser extensions detected. Consider disabling unnecessary ones.`,
      icon: 'alert'
    });
  }
  
  return alerts;
};
